import React, { useState, useEffect } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import EditAndApproveForm from "./EditAndApproveForm";

const Approvals = () => {
  const [pendingListings, setPendingListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingListing, setEditingListing] = useState(null);

  // Fetch pending listings on load
  useEffect(() => {
    const fetchPendingListings = async () => {
      try {
        const q = query(
          collection(db, "listings"),
          where("status", "==", "Pending Approval")
        );
        const querySnapshot = await getDocs(q);
        const listings = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPendingListings(listings);
      } catch (error) {
        console.error("Error fetching pending listings: ", error);
      }
    };

    fetchPendingListings();
  }, []);

  // Fetch selected user details
  useEffect(() => {
    const fetchUser = async () => {
      if (selectedListing && selectedListing.userID) {
        try {
          const userRef = doc(db, "users", selectedListing.userID);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setSelectedUser(userSnap.data());
          } else {
            setSelectedUser(null);
          }
        } catch (error) {
          console.error("Error fetching user data: ", error);
        }
      }
    };
    fetchUser();
  }, [selectedListing]);

  // Reject a listing
  const handleReject = async (id) => {
    try {
      const listingRef = doc(db, "listings", id);
      await updateDoc(listingRef, { status: "Rejected" });

      alert("Listing rejected!");
      setPendingListings((prev) => prev.filter((listing) => listing.id !== id));
      if (selectedListing?.id === id) setSelectedListing(null);
    } catch (error) {
      console.error("Error rejecting listing: ", error);
    }
  };

  // Handle Approve & Edit
  const handleApproveAndEdit = (listing) => {
    setEditingListing(listing); // Open the EditAndApproveForm with the selected listing
  };

  // Handle form submission from EditAndApproveForm
  const handleEditComplete = async (updatedListing) => {
    try {
      setEditingListing(null); // Close the form

      if (updatedListing) {
        // Update the listing status and save any changes
        const listingRef = doc(db, "listings", updatedListing.id);
        await updateDoc(listingRef, { ...updatedListing, status: "Approved" });

        // Move the listing to the cars collection
        const carData = { ...updatedListing, moneyRaised: updatedListing.initialInvestment };
        delete carData.id;
        delete carData.listingID;
        delete carData.status;

        await addDoc(collection(db, "cars"), carData);

        // Record the investment in the investments collection
        const investmentData = {
          amount: updatedListing.initialInvestment,
          carID: updatedListing.id,
          date: Timestamp.now(),
          userID: updatedListing.userID,
        };
        await addDoc(collection(db, "investments"), investmentData);

        // Update pending listings
        setPendingListings((prev) =>
          prev.filter((listing) => listing.id !== updatedListing.id)
        );

        alert("Listing approved and added to the marketplace!");
      }
    } catch (error) {
      console.error("Error completing the edit/approval process:", error);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h1>Admin Dashboard</h1>
      {pendingListings.length === 0 ? (
        <p>No pending listings.</p>
      ) : (
        <div style={{ display: "flex", gap: "20px" }}>
          {/* Table Section */}
          <div style={{ flex: 1 }}>
            <table style={{ width: "100%", border: "1px solid #ddd" }}>
              <thead>
                <tr>
                  <th>Car</th>
                  <th>Year</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingListings.map((listing) => (
                  <tr key={listing.id}>
                    <td>{listing.carName || `${listing.carMake} ${listing.carModel}`}</td>
                    <td>{listing.year || "N/A"}</td>
                    <td>${listing.price ? listing.price.toLocaleString() : "N/A"}</td>
                    <td>{listing.status}</td>
                    <td>
                      <button
                        onClick={() => setSelectedListing(listing)}
                        style={{ marginRight: "5px" }}
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleApproveAndEdit(listing)}
                        style={{ marginRight: "5px" }}
                      >
                        Approve & Edit
                      </button>
                      <button onClick={() => handleReject(listing.id)}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Details Section */}
          <div
            style={{
              flex: 1,
              border: "1px solid #ddd",
              padding: "20px",
              borderRadius: "8px",
              background: "black",
            }}
          >
            <h2>Listing Details</h2>
            {selectedListing ? (
              <div>
                <p>
                  <strong>Listing ID:</strong> {selectedListing.listingID || "N/A"}
                </p>
                <p>
                  <strong>User ID:</strong> {selectedListing.userID || "N/A"}
                </p>
                <p>
                  <strong>Car Name:</strong>{" "}
                  {selectedListing.carName ||
                    `${selectedListing.carMake} ${selectedListing.carModel}`}
                </p>
                <p>
                  <strong>Year:</strong> {selectedListing.year || "N/A"}
                </p>
                <p>
                  <strong>Total Value:</strong>{" "}
                  {selectedListing.totalValue
                    ? `$${Number(selectedListing.totalValue).toLocaleString()}`
                    : "N/A"}
                </p>
                <p>
                  <strong>Location:</strong> {selectedListing.location || "N/A"}
                </p>
                <p>
                  <strong>Initial Investment:</strong>{" "}
                  ${selectedListing.initialInvestment?.toLocaleString() || "N/A"}
                </p>
                <p>
                  <strong>Service Fee:</strong>{" "}
                  ${selectedListing.serviceFee?.toLocaleString() || "N/A"}
                </p>
                <p>
                  <strong>Listing Price:</strong>{" "}
                  ${selectedListing.listingPrice?.toLocaleString() || "N/A"}
                </p>
                <p>
                  <strong>Total Due:</strong>{" "}
                  ${selectedListing.totalDue?.toLocaleString() || "N/A"}
                </p>
                <p>
                  <strong>Summary:</strong> {selectedListing.summary || "N/A"}
                </p>
                {selectedListing.pictures && selectedListing.pictures.length > 0 ? (
                  <div>
                    <strong>Pictures:</strong>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: "10px",
                      }}
                    >
                      {selectedListing.pictures.map((pic, index) => (
                        <img
                          key={index}
                          src={pic}
                          alt={`Picture ${index + 1}`}
                          style={{ maxWidth: "100px", maxHeight: "100px" }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <p>
                    <strong>Pictures:</strong> None
                  </p>
                )}
              </div>
            ) : (
              <p>Select a listing to view its details.</p>
            )}
          </div>
        </div>
      )}

      {editingListing && (
        <EditAndApproveForm
          listingID={editingListing.id}
          onClose={handleEditComplete}
        />
      )}
    </div>
  );
};

export default Approvals;
